// frontend/app/components/lessonPlans/LessonBlockRenderer.js
//
// Dispatches one authored block to its renderer. Adding a block type means
// adding a case here and a component beside it — never a lesson-specific
// branch (anti-goal #4) and never a slug switch.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import {
  AiPromptBlock,
  BulletListBlock,
  CalloutBlock,
  RichTextBlock,
  TeacherTipBlock,
} from "./blocks/LessonTextBlocks";
import { PwoBlock, TopicQuizBlock, VideoBlock } from "./blocks/LessonAssetBlocks";
import { assetSummaryFor } from "./assetSummary";
import { quizAnswersFor } from "./lessonPlanRules";
import {
  MultiSelectBlock,
  MultipleChoiceBlock,
  OrderingBlock,
  TextResponseBlock,
} from "./blocks/LessonCheckBlocks";
import {
  ExplanationBlock,
  ExplorationProbeBlock,
  MasteryMapBlock,
  NextChallengeBlock,
  ReflectionBlock,
} from "./blocks/LessonLoopBlocks";

const UnknownBlock = ({ block, colors, t }) => {
  const translate = t || ((value) => value);
  return (
    <View
      style={[
        styles.unknown,
        { borderColor: colors.border, backgroundColor: colors.subtleBg },
      ]}
    >
      <Text style={[styles.unknownText, { color: colors.textMuted }]}>
        {translate("Unsupported content block")}: {block?.type || "?"}
      </Text>
    </View>
  );
};

const LessonBlockRenderer = ({
  block,
  colors,
  isDarkMode,
  mode = "teacher",
  sessionState,
  conceptLabels,
  readOnly = false,
  launchingBlockId = null,
  // The student's own concept bands, for a mastery_map block. Absent is a
  // legitimate state — the block renders its own "nothing yet" copy.
  mastery = null,
  onAnswer,
  onCheck,
  onLaunch,
  onAsk,
  // Puts an authored asset summary in the Saras rail, drawn there as a question
  // and an answer by the rail's own exchange rendering. Separate from onAsk on
  // purpose: onAsk sends text to the model, and this text IS the answer.
  //
  // Optional, and unwired the control is not drawn at all. There is no second
  // rendering to fall back to — see the note on AssetCard's `onSummary`.
  onShowSummary,
  onHintRequest,
  // Speaking an answer needs a language for the transcriber and a sink for the
  // "answered by voice" signal. Both optional: without them the free-text
  // blocks are typed, exactly as before.
  language = "en",
  onExplorationEvent,
  t,
}) => {
  if (!block || typeof block !== "object") return null;

  const entry = (sessionState && sessionState[block.id]) || null;
  const loading = launchingBlockId === block.id;

  const shared = { block, colors, isDarkMode, entry, t };
  const checkProps = { ...shared, onAnswer, onCheck, readOnly, conceptLabels };
  // The free-text blocks additionally offer speech where the runtime allows it.
  const writtenProps = { ...checkProps, language, onExplorationEvent };
  // Where an asset summary goes when it is pressed. An authored one is already
  // the answer, so it goes to the rail as it stands; an authored prompt is a
  // question, so it takes the same route an ai_prompt question takes.
  //
  // The handler exists only where that route does. Nothing here may hand a card
  // a callback that would land nowhere — the card draws its control off this
  // prop, so the prop has to mean what it says.
  const summary = assetSummaryFor(block);
  const summaryRoute =
    summary && (summary.kind === "prompt" ? onAsk : onShowSummary)
      ? (pressed) => {
          if (pressed.kind === "prompt") onAsk(pressed.prompt);
          else onShowSummary(pressed, block);
        }
      : undefined;

  const assetProps = {
    ...shared,
    loading,
    onLaunch: () => onLaunch && onLaunch(block),
    onSummary: summaryRoute,
  };

  switch (block.type) {
    case "rich_text":
      return <RichTextBlock {...shared} />;
    case "bullet_list":
      return <BulletListBlock {...shared} />;
    case "callout":
      return <CalloutBlock {...shared} />;
    case "teacher_tip":
      return <TeacherTipBlock {...shared} mode={mode} />;
    case "ai_prompt":
      return <AiPromptBlock {...shared} onAsk={onAsk} />;

    case "saras_pwo":
      return <PwoBlock {...assetProps} />;
    case "saras_video":
      return <VideoBlock {...assetProps} />;
    case "saras_topic_quiz":
      // A container: its progress is the answers stored under its questions'
      // composite ids, not anything in its own session entry.
      return (
        <TopicQuizBlock
          {...assetProps}
          answeredCount={quizAnswersFor(sessionState, block.id).length}
        />
      );

    case "multiple_choice":
      return <MultipleChoiceBlock {...checkProps} />;
    case "multi_select":
      return <MultiSelectBlock {...checkProps} />;
    case "ordering":
      return <OrderingBlock {...checkProps} />;
    case "short_answer":
    case "prediction":
      return <TextResponseBlock {...writtenProps} />;

    case "explanation":
      return <ExplanationBlock {...writtenProps} />;
    case "exploration_probe":
      return <ExplorationProbeBlock {...writtenProps} onHintRequest={onHintRequest} />;
    case "reflection":
      return <ReflectionBlock {...writtenProps} />;
    case "next_challenge":
      return <NextChallengeBlock {...writtenProps} />;
    case "mastery_map":
      return <MasteryMapBlock {...shared} mastery={mastery} />;

    default:
      return <UnknownBlock block={block} colors={colors} t={t} />;
  }
};

const styles = StyleSheet.create({
  unknown: {
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "dashed",
    padding: 12,
    marginBottom: 12,
  },
  unknownText: { fontSize: 12 },
});

export default LessonBlockRenderer;
